import {create} from "@bufbuild/protobuf";
import {createClient} from "@connectrpc/connect";
// The shared anonymous fire-and-forget transport — the same one the consent, vitals and recipient counters
// use, so this counter keeps their promises rather than restating them.
import {fireBeacon} from "../../api/beaconTransport";
import {
	FunnelEventService,
	ReportPersonalizationEventRequestSchema,
} from "../../gen/kitecorp/transferify/common/v1/funnel_events_pb";
import type {PersonalizationEvent, PersonalizationEventEmitter, Unsubscribe} from "./personalizationEvents";

/**
 * The telemetry SUBSCRIBER for the personalization funnel (ui#1814).
 *
 * Separate from {@link ./personalizationEvents} on purpose (owner 2026-08-27): the flow emits domain
 * events and knows nothing about where they go, so measuring the funnel is never a reason to reach into
 * the builder or the send offer. This module is the whole of "where they go".
 *
 * Fire-and-forget, uncorrelatable and never retried, exactly like its siblings: a retry would
 * double-count precisely the events that did arrive, and these counters carry no request identity to
 * deduplicate with.
 *
 * **The api half is api#1115, and it shipped.** This counter was deliberately released ahead of it: a call
 * the api does not answer is dropped by the transport, the same thing it does for any transient failure, so
 * nothing in the sender's flow ever waited on it or showed anything when it failed. (This paragraph named
 * api#999 — the AUTHENTICATED offer beacon, a different endpoint — and called it open long after both had
 * merged. A comment asserting the status of another repo's ticket rots silently and then reads as an
 * instruction, so this one names which ticket built it and stops there.)
 */

/** The complete request body: one counter, and the moment it happened at. */
export interface PersonalizationEventReport {
	counter: PersonalizationEvent["counter"];
	moment: PersonalizationEvent["moment"];
}

/** One event as the wire carries it. A pure function, so the shape is testable without a transport. */
export function personalizationEventReport(event: PersonalizationEvent): PersonalizationEventReport {
	return {counter: event.counter, moment: event.moment};
}

/**
 * Sends one event, and returns whether anything left the browser.
 *
 * Exported for the same reason the report builder is: "did this counter fire" is the thing worth
 * asserting, and it is not observable from the emitter alone.
 */
export function reportPersonalizationEvent(event: PersonalizationEvent): boolean {
	return fireBeacon(transport => createClient(FunnelEventService, transport).reportPersonalizationEvent(
		create(ReportPersonalizationEventRequestSchema, personalizationEventReport(event))));
}

/**
 * Subscribes the beacon to an emitter and returns the unsubscribe.
 *
 * Wired from `CreateFlow`, which is the surface that OWNS this whole funnel: it stays mounted from the
 * landing through the four destination sheets and across the create/progress/success machine, so every
 * counter this beacon can receive is emitted while it is alive. A narrower host — the panel, the builder
 * — would unsubscribe exactly where the funnel gets interesting, because both of those unmount partway
 * through it.
 */
export function wirePersonalizationTelemetry(emitter: PersonalizationEventEmitter): Unsubscribe {
	return emitter.subscribe(event => {
		reportPersonalizationEvent(event);
	});
}
